import { useState, useRef, useEffect, useLayoutEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { ArrowUp, ChevronDown, Heart, MapPin, Lightbulb } from "lucide-react";
import { updatePageMetadata, pageMetadata } from "../utils/pageMetadata";
import ChatPlaceCard from "./ChatPlaceCard";
import PlaceDetailPanel from "./PlaceDetailPanel";
import PlaceholderAnimation from "./PlaceholderAnimation";
import QuickTipsOverlay from "./QuickTipsOverlay";
import { Message, Result, ChatUIProps, StructuredSection } from "../types/chat";
import { useTypingAnimation } from "../hooks/useTypingAnimation";
import { CATEGORY_MESSAGES } from "../constants/chatMessages";
import { useAuth } from "../contexts/AuthContext";
import { useChatContext } from "../contexts/ChatContext";
import { usePermissions } from "../hooks/usePermissions";
import { useSavedPlaces } from "../contexts/SavedPlacesContext";
import RegistrationPrompt from "./RegistrationPrompt";
import { RateLimitError } from "../types/permissions";
import { API_BASE_URL } from "../config";

/**
 * Check if text contains inappropriate/explicit content
 */
function containsInappropriateContent(text: string): boolean {
  const lowerText = text.toLowerCase();
  
  // Basic profanity filter - expand this list as needed
  const inappropriateWords = [
    'fuck', 'shit', 'bitch', 'damn', 'bastard', 'ass', 
    'dick', 'cock', 'pussy', 'whore', 'slut', 'cunt',
    'nigger', 'nigga', 'faggot', 'retard', 'piss',
    // Add more words as needed
  ];
  
  // Abusive phrases - check for multi-word patterns
  const abusivePhrases = [
    'go to hell',
    'go fuck yourself',
    'fuck you',
    'fuck off',
    'shut up',
    'shut the fuck up',
    'piss off',
    'screw you',
    'damn you',
    'kill yourself',
    'kys', // internet slang for "kill yourself"
    'piece of shit',
    'son of a bitch',
    'mother fucker',
    'motherfucker',
    'eat shit',
    'suck my',
    'suck it',
    'blow me',
    'kiss my ass',
    'shove it',
    'get lost',
    'drop dead',
    'go die',
    'i hate you',
    'you suck',
    'this sucks',
    'you\'re useless',
    'you\'re worthless',
    'you\'re stupid',
    'fucking idiot',
    'dumb ass',
    'dumbass',
    'jackass',
    // Add more phrases as needed
  ];
  
  // Check for abusive phrases
  for (const phrase of abusivePhrases) {
    if (lowerText.includes(phrase)) {
      return true;
    }
  }
  
  // Check for exact word matches with word boundaries
  for (const word of inappropriateWords) {
    // Use regex with word boundaries to avoid false positives
    // e.g., "class" shouldn't match "ass"
    const regex = new RegExp(`\\b${word}\\b`, 'i');
    if (regex.test(lowerText)) {
      return true;
    }
  }
  
  // Optional: Add pattern matching for common obfuscation
  // e.g., f*ck, sh!t, etc.
  const obfuscationPatterns = [
    /f[\*\@\#]ck/i,
    /sh[\*\@\#]t/i,
    /b[\*\@\#]tch/i,
    /d[\*\@\#]ck/i,
    /[\*\@\#]ss/i,
  ];
  
  for (const pattern of obfuscationPatterns) {
    if (pattern.test(lowerText)) {
      return true;
    }
  }
  
  return false;
}

/**
 * Check if text is a greeting/conversation starter
 */
function isGreeting(text: string): boolean {
  const trimmed = text.trim().toLowerCase();
  
  // Single word greetings
  const greetingWords = [
    'hi', 'hello', 'hey', 'hola', 'greetings', 
    'yo', 'sup', 'hiya', 'hey','howdy', 'ayubowan',
  ];
  
  // Check for exact single word match
  if (greetingWords.includes(trimmed)) {
    return true;
  }
  
  // Greeting phrases
  const greetingPhrases = [
    'good morning',
    'good afternoon',
    'good evening',
    'good day',
    'how are you',
    'how r u',
    'how do you do',
    'what\'s up',
    'whats up',
    'wassup',
    'how are things',
    'how\'s it going',
    'how is it going',
    'how you doing',
    'nice to meet you',
    'pleased to meet you',
    'hi there',
    'hey there',
    'hello there',
  ];
  
  for (const phrase of greetingPhrases) {
    if (trimmed === phrase || trimmed.startsWith(phrase + ' ') || trimmed.endsWith(' ' + phrase)) {
      return true;
    }
  }
  
  return false;
}

/**
 * Fetch a curated greeting from the backend
 */
async function fetchCuratedGreeting(): Promise<{ response: string, suggestions: string[], placeIds?: string[] } | null> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/chat/greeting`);
    const data = await response.json();
    
    if (data.success && data.response) {
      return {
        response: data.response,
        suggestions: data.suggestions || [],
        placeIds: data.placeIds || []
      };
    }
    return null;
  } catch (error) {
    return null;
  }
}

/**
 * Get greeting message (curated or fallback)
 */
async function getGreetingMessage(): Promise<{ text: string, suggestions: string[] }> {
  const curated = await fetchCuratedGreeting();
  
  if (curated) {
    return {
      text: curated.response,
      suggestions: curated.suggestions
    };
  }
  
  // Fallback to hardcoded greeting  
  return {
    text: "🙏 Ayubowan! Welcome to Hey Sri Lanka! 🇱🇰\n\nI'm your personal travel assistant for exploring Sri Lanka. I can help you discover:\n\n✨ Amazing attractions and hidden gems\n🍽️ Delicious local food spots\n🏨 Perfect places to stay\n🎯 Unique experiences and adventures\n\nJust ask me anything about traveling in Sri Lanka, and I'll help you plan your perfect trip!",
    suggestions: [
      "What are the best beaches in Sri Lanka?",
      "Show me things to do in Kandy",
      "Where can I try authentic Sri Lankan food?"
    ]
  };
}

/**
 * Component to render a structured section
 */
function StructuredSectionComponent({ section }: { section: StructuredSection }) {
  const { type, title, content, icon } = section;

  const renderContent = () => {
    if (type === "list" && Array.isArray(content)) {
      return (
        <ul className="list-disc list-inside space-y-1">
          {content.map((item, index) => (
            <li key={index} className="text-sm">
              {typeof item === 'string' ? item : JSON.stringify(item)}
            </li>
          ))}
        </ul>
      );
    }
    
    // Handle different content types
    if (typeof content === 'string') {
      return <span className="text-sm" style={{ whiteSpace: 'pre-line' }}>{content}</span>;
    } else if (typeof content === 'object' && content !== null) {
      // If content is an object, convert it to a readable string
      return <span className="text-sm" style={{ whiteSpace: 'pre-wrap' }}>{JSON.stringify(content, null, 2)}</span>;
    } else {
      return <span className="text-sm" style={{ whiteSpace: 'pre-line' }}>{String(content)}</span>;
    }
  };

  const getSectionStyles = () => {
    switch (type) {
      case "highlight":
        return "bg-gray-100 border-l-4 border-gray-700 p-3 rounded-r-lg";
      case "info":
        return "bg-gray-50 border-l-4 border-gray-600 p-3 rounded-r-lg";
      case "list":
        return "bg-gray-50 p-3 rounded-lg";
      default:
        return "p-2";
    }
  };

  return (
    <div className={`${getSectionStyles()} mb-2`}>
      {title && (
        <div className="flex items-center gap-2 mb-2">
          {icon && <span className="text-lg">{icon}</span>}
          <h4 className="font-semibold text-sm text-gray-800">{title}</h4>
        </div>
      )}
      {!title && icon && (
        <div className="flex items-center gap-2 mb-1">
          <span className="text-lg">{icon}</span>
        </div>
      )}
      {renderContent()}
    </div>
  );
}

export default function ChatUI({ setMapMarkers, highlightedId, setHighlightedId, setHoveredId, setOnMapMarkerClick, setCarouselState, setCarouselHandlers, setCarouselData }: ChatUIProps) {
  const { authState } = useAuth(); // Get authenticated user
  const user = authState.user; // Extract user from authState
  const { messages, setMessages, isLoadingHistory, setIsLoadingHistory, hasMoreHistory, setHasMoreHistory, historyOffset, setHistoryOffset, loadOlderHistory: loadOlderHistoryContext } = useChatContext();
  const { tier, canChat, recordChatUsage } = usePermissions();
  const { isPlaceSaved, savePlace, unsavePlace } = useSavedPlaces(); // Access saved places context
  const location = useLocation(); // Track navigation
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [waitingMessage, setWaitingMessage] = useState("");
  const [typingComplete, setTypingComplete] = useState<{ [key: string]: boolean }>({}); // Track which messages finished typing by messageId
  const [showScrollButton, setShowScrollButton] = useState(false); // Show scroll to bottom button
  const [detailPanelPlace, setDetailPanelPlace] = useState<Result | null>(null); // Track which place to show in detail panel
  const [touchedPlaceId, setTouchedPlaceId] = useState<string | number | null>(null); // Track touched place for mobile overlay
  const [showRegistrationPrompt, setShowRegistrationPrompt] = useState(false);
  const [registrationVariant, setRegistrationVariant] = useState<'google_auth' | 'whatsapp_verify' | 'wait_only'>('google_auth');
  const [registrationTrigger, setRegistrationTrigger] = useState<string>('chat_limit');
  const [rateLimitWaitMinutes, setRateLimitWaitMinutes] = useState<number | undefined>(undefined);
  const [showQuickTips, setShowQuickTips] = useState(false); // Quick Tips overlay state
  
  // Mobile map carousel state
  const [activeCardIndex, setActiveCardIndex] = useState(0);
  const [cardSwipeOffset, setCardSwipeOffset] = useState(0);
  const [isCardSwiping, setIsCardSwiping] = useState(false);
  const [hoveredCardId, setHoveredCardId] = useState<string | number | null>(null); // For hover highlighting
  
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const placeRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const isLoadingHistoryRef = useRef(false); // Track if we're loading history to prevent auto-scroll
  const messagesContainerRef = useRef<HTMLDivElement | null>(null); // Ref to the scrollable container
  const scrollRestoreRef = useRef<{ previousScrollHeight: number; previousScrollTop: number } | null>(null); // Track scroll position to restore
  const highlightSourceRef = useRef<'map' | 'card' | null>(null); // Track where the highlight came from
  const latestChatMarkersRef = useRef<any[]>([]); // Store latest chat markers to persist on map
  const hasScrolledInitiallyRef = useRef(false); // Track if we've scrolled to bottom after initial load
  const previousPathnameRef = useRef<string>(''); // Track previous pathname for navigation detection
  const cardContainerRef = useRef<HTMLDivElement | null>(null); // Ref to carousel container
  const cardStartXRef = useRef(0); // Track initial touch X position
  const cardCurrentXRef = useRef(0); // Track current touch X position
  const activeCardIndexRef = useRef(0); // Keep track of current active card index for swipe handlers

  // CLIENT-SIDE CHAT PERSISTENCE FOR UNREGISTERED USERS
  const CHAT_STORAGE_KEY = 'hey-sl-chat-history';
  
  // Update page metadata on mount
  useEffect(() => {
    updatePageMetadata(pageMetadata.chat.title, pageMetadata.chat.description);
  }, []);
  
  // Load chat history from localStorage on mount (unregistered users only)
  useEffect(() => {
    if (tier === 'unregistered' && messages.length === 0) {
      const storedChats = localStorage.getItem(CHAT_STORAGE_KEY);
      if (storedChats) {
        try {
          const parsed = JSON.parse(storedChats);
          // Mark all loaded messages as "from history" to prevent typing animation
          const messagesWithHistoryFlag = parsed.map((msg: Message) => ({
            ...msg,
            isFromHistory: true
          }));
          setMessages(messagesWithHistoryFlag);
        } catch (error) {
          // Error parsing stored chat history
          localStorage.removeItem(CHAT_STORAGE_KEY);
        }
      } else {
        // No stored chat history - show welcome message for new unregistered users
        // Fetch curated greeting asynchronously
        getGreetingMessage().then(greeting => {
          const welcomeMessage: Message = {
            role: "bot",
            text: greeting.text,
            messageId: `welcome_${Date.now()}`,
            isFromHistory: true, // Don't animate
            suggestions: greeting.suggestions
          };
          
          setMessages([welcomeMessage]);
        });
      }
    }
  }, [tier]); // Only run when tier changes or on mount

  // Save chat history to localStorage whenever messages change (unregistered users only)
  useEffect(() => {
    if (tier === 'unregistered' && messages.length > 0) {
      try {
        localStorage.setItem(CHAT_STORAGE_KEY, JSON.stringify(messages));
      } catch (error) {
        // Error saving chat history to localStorage
      }
    }
  }, [messages, tier]);

  // Clear localStorage chat history when user registers (transitions from unregistered to registered)
  useEffect(() => {
    if (tier !== 'unregistered' && messages.length > 0) {
      const storedChats = localStorage.getItem(CHAT_STORAGE_KEY);
      if (storedChats) {
        localStorage.removeItem(CHAT_STORAGE_KEY);
      }
    }
  }, [tier, messages.length]);

  // Scroll to bottom function
  const scrollToBottom = useCallback((instant = false) => {
    bottomRef.current?.scrollIntoView({ behavior: instant ? "auto" : "smooth" });
  }, []);

  // Check if user is near the bottom of the chat (within 100px)
  const isNearBottom = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return true; // Default to true if container not available
    
    const threshold = 100; // pixels from bottom
    const position = container.scrollTop + container.clientHeight;
    const height = container.scrollHeight;
    
    return position >= height - threshold;
  }, []);

  // Handle scroll to check if we should show the scroll button
  const handleScroll = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    
    const threshold = 200; // Show button if more than 200px from bottom
    const position = container.scrollTop + container.clientHeight;
    const height = container.scrollHeight;
    const isAtBottom = position >= height - threshold;
    
    setShowScrollButton(!isAtBottom);
  }, []);

  // Smart scroll - only scroll if user is near bottom
  const smartScrollToBottom = useCallback(() => {
    if (isNearBottom()) {
      scrollToBottom();
    }
  }, [isNearBottom, scrollToBottom]);

  // SCROLL SCENARIO 1: Initial load - scroll to bottom when messages first populate
  useEffect(() => {
    if (
      !hasScrolledInitiallyRef.current && 
      messages.length > 0 && 
      !isLoadingHistoryRef.current
    ) {
      // Messages just loaded for the first time, scroll to bottom
      // Use longer timeout to ensure DOM is fully rendered with all content
      setTimeout(() => {
        const container = messagesContainerRef.current;
        if (container) {
          // Force scroll to bottom using scrollTop for reliability
          container.scrollTop = container.scrollHeight;
        }
        hasScrolledInitiallyRef.current = true;
      }, 200);
    }
  }, [messages.length]);

  // SCROLL SCENARIO 2: Navigation - scroll to bottom when returning to chat from another page
  useEffect(() => {
    const currentPath = location.pathname;
    const previousPath = previousPathnameRef.current;
    
    // Check if we're navigating TO chat FROM a different page
    if (
      currentPath.includes('/chat') && 
      previousPath && 
      !previousPath.includes('/chat') && 
      messages.length > 0 && 
      hasScrolledInitiallyRef.current && // Only if we've already done initial load
      !isLoadingHistoryRef.current
    ) {
      setTimeout(() => {
        scrollToBottom();
      }, 100);
    }
    
    // Update previous path for next comparison
    previousPathnameRef.current = currentPath;
  }, [location.pathname, scrollToBottom, messages.length]);

  // SCROLL SCENARIO 3: New messages during active session - auto-scroll if near bottom
  useEffect(() => {
    if (
      !isLoadingHistoryRef.current && 
      hasScrolledInitiallyRef.current && // Only after initial load
      isNearBottom()
    ) {
      scrollToBottom();
    }
  }, [messages, loading, scrollToBottom, isNearBottom]);

  // Restore scroll position after loading older messages (runs synchronously after DOM update)
  useLayoutEffect(() => {
    if (scrollRestoreRef.current && messagesContainerRef.current) {
      const container = messagesContainerRef.current;
      const { previousScrollHeight, previousScrollTop } = scrollRestoreRef.current;
      
      const newScrollHeight = container.scrollHeight;
      const heightDifference = newScrollHeight - previousScrollHeight;
      container.scrollTop = previousScrollTop + heightDifference;
      
      // Clear the restore ref
      scrollRestoreRef.current = null;
    }
  }, [messages]); // Run after messages update

  // Only scroll to highlighted card if highlight came from map (external), not from card hover
  useEffect(() => {
    if (highlightedId != null) {
      const el = placeRefs.current[highlightedId];
      // Only scroll if source is NOT 'card' (i.e., came from map or null/external)
      if (el && highlightSourceRef.current !== 'card') {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }
    // Reset source after handling
    highlightSourceRef.current = null;
  }, [highlightedId]);

  // Attach scroll listener to show/hide scroll button
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    
    container.addEventListener('scroll', handleScroll);
    
    // Check initial state
    handleScroll();
    
    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  // Note: Chat history loading is now handled by ChatContext
  // The context automatically loads history when user logs in

  // Show welcome greeting for users with no chat history
  useEffect(() => {
    // Only show greeting if:
    // 1. Messages are empty
    // 2. User is authenticated (tier !== 'unregistered')
    // 3. We're not currently loading history
    // 4. We've tried to load history (isLoadingHistory was true at some point, now false)
    if (messages.length === 0 && tier !== 'unregistered' && !isLoadingHistory && user?.userId) {
      // Fetch curated greeting asynchronously
      getGreetingMessage().then(greeting => {
        // Double-check messages are still empty before showing greeting
        setMessages((prevMessages) => {
          if (prevMessages.length === 0) {
            const welcomeMessage: Message = {
              role: "bot",
              text: greeting.text,
              messageId: `welcome_${Date.now()}`,
              isFromHistory: true, // Don't animate
              suggestions: greeting.suggestions
            };
            
            return [welcomeMessage];
          } else {
            return prevMessages;
          }
        });
      });
    }
  }, [messages.length, tier, isLoadingHistory, user?.userId]);

  // Load older history with scroll preservation
  async function loadOlderHistory() {
    const container = messagesContainerRef.current;
    if (!container) return;
    
    // Save scroll position before loading - will be restored by useLayoutEffect
    scrollRestoreRef.current = {
      previousScrollHeight: container.scrollHeight,
      previousScrollTop: container.scrollTop
    };
    
    setIsLoadingHistory(true);
    isLoadingHistoryRef.current = true; // Prevent auto-scroll
    
    try {
      await loadOlderHistoryContext();
    } catch (error) {
      // Error loading older history
      scrollRestoreRef.current = null; // Clear on error
    } finally {
      // Reset the flag after a brief delay to allow React to finish rendering
      setTimeout(() => {
        isLoadingHistoryRef.current = false;
      }, 100);
    }
  }

  async function sendMessage(text?: string, suggestionIndex?: number, isFromGreeting?: boolean, isFromCuratedResponse?: boolean) {
    const msgText = text || input;
    if (!msgText.trim()) return;

    // Prevent sending while another message is in flight
    if (loading) {
      return;
    }

    // Check for greetings first
    if (isGreeting(msgText)) {
      
      // Generate unique message IDs for this exchange
      const userMessageId = `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const botMessageId = `bot_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      // Add user's message
      const userMsg: Message = { role: "user", text: msgText, messageId: userMessageId };
      setMessages((prev) => [...prev, userMsg]);
      setInput(""); // Clear input
      
      // Fetch and show curated greeting
      getGreetingMessage().then(greeting => {
        const welcomeMessage: Message = {
          role: "bot",
          text: greeting.text,
          messageId: botMessageId,
          suggestions: greeting.suggestions,
          results: [],
          isFromHistory: true, // Skip typing animation
          isFromGreeting: true // Mark as greeting for special handling
        };
        setMessages((prev) => [...prev, welcomeMessage]);
        
        // Scroll to show the response
        setTimeout(() => scrollToBottom(), 100);
      });
      
      return; // Stop processing
    }

    // Check for inappropriate/explicit content
    if (containsInappropriateContent(msgText)) {
      // Generate unique message IDs for this exchange
      const userMessageId = `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const botMessageId = `bot_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      // Add user's message (so they can see what they sent)
      const userMsg: Message = { role: "user", text: msgText, messageId: userMessageId };
      setMessages((prev) => [...prev, userMsg]);
      setInput(""); // Clear input
      
      // Add static warning response
      const warningMessage: Message = {
        role: "bot",
        text: "⚠️ Please rephrase your question respectfully. Hey SriLanka is here to help you explore Sri Lanka with appropriate language.",
        messageId: botMessageId,
        suggestions: [
          "Show me attractions in Colombo",
          "Where can I find good food?",
          "What are the best hotels?"
        ],
        results: [],
        isFromHistory: true // Skip typing animation
      };
      setMessages((prev) => [...prev, warningMessage]);
      
      // Scroll to show the response
      setTimeout(() => scrollToBottom(), 100);
      return; // Stop processing
    }

    // Check permissions before sending
    const chatPermission = canChat();
    if (!chatPermission.allowed) {
      if (tier === 'unregistered') {
        setRegistrationVariant('google_auth');
        setShowRegistrationPrompt(true);
      } else {
        setRegistrationVariant('whatsapp_verify');
        setShowRegistrationPrompt(true);
      }
      return;
    }

    // Generate unique message IDs
    const userMessageId = `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const botMessageId = `bot_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    // Add user message IMMEDIATELY for instant feedback
    const userMsg: Message = { role: "user", text: msgText, messageId: userMessageId };
    setMessages((prev) => [...prev, userMsg]);
    setInput(""); // Clear input immediately for instant feedback
    setLoading(true);
    
    // Force scroll to bottom when user sends a message (they initiated this action)
    // Use longer timeout to ensure React has rendered the new message, with auto scroll for immediate response feel
    setTimeout(() => {
      bottomRef.current?.scrollIntoView({ behavior: "auto" });
    }, 50);

    // For registered users, check server-side limit AFTER showing the message
    if (tier !== 'unregistered' && user?.userId) {
      try {
        const checkRes = await fetch(`${API_BASE_URL}/api/chat/check-limit`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId: user.userId })
        });
        
        const checkResult = await checkRes.json();
        
        if (!checkResult.allowed) {
          setLoading(false);
          
          // Add error message
          const errorMessage: Message = {
            role: "bot",
            text: "⚠️ You've reached your daily chat limit. Please verify your phone number to continue chatting.",
            messageId: botMessageId,
            suggestions: [],
            results: [],
            isFromHistory: true
          };
          setMessages((prev) => [...prev, errorMessage]);
          
          setRegistrationVariant('whatsapp_verify');
          setRateLimitWaitMinutes(checkResult.waitMinutes);
          setShowRegistrationPrompt(true);
          return;
        }
      } catch (error) {
        // Error checking chat limit
        // Continue anyway if check fails
      }
    }

    // Generate category-specific waiting message
    const getCategoryMessage = (category: string = 'GENERAL') => {
      const categoryMessages = CATEGORY_MESSAGES[category as keyof typeof CATEGORY_MESSAGES] || CATEGORY_MESSAGES.GENERAL;
      return categoryMessages[Math.floor(Math.random() * categoryMessages.length)];
    };

    // Set initial waiting message (will update with actual category after API call)
    setWaitingMessage(getCategoryMessage('GENERAL'));

    // Get previous conversation (last user + bot message pair) for suggestion context
    let previousConversation = null;
    if (suggestionIndex && messages.length >= 2) {
      const lastBotMsg = messages[messages.length - 1];
      const lastUserMsg = messages[messages.length - 2];
      
      if (lastBotMsg.role === 'bot' && lastUserMsg.role === 'user') {
        previousConversation = {
          userMsg: lastUserMsg.text,
          botReply: lastBotMsg.text
        };
      }
    }

    try {
      // Get initial response (fast - just chat, no place cards yet)
      const res = await fetch(`${API_BASE_URL}/api/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          message: msgText,
          userId: user?.userId || null,
          suggestionIndex: suggestionIndex !== undefined ? suggestionIndex : null,
          isFromGreeting: isFromGreeting || false,
          isFromCuratedResponse: isFromCuratedResponse || false,
          previousConversation: previousConversation
        }),
      });

      if (!res.ok) {
        // Handle rate limit error
        if (res.status === 429) {
          const errorData: RateLimitError = await res.json();
          
          // IMMEDIATELY clear loading state and waiting message
          setLoading(false);
          setWaitingMessage('');
          
          // Remove the user message that failed
          setMessages((prev) => prev.filter(m => m.messageId !== userMessageId));
          
          // Determine user type and appropriate message/variant
          const isVerified = user?.whatsappNumberE164 || user?.telegramNumberE164;
          const isRegistered = !!user?.userId;
          
          let blockMessageText: string;
          let promptVariant: 'google_auth' | 'whatsapp_verify' | 'wait_only';
          
          if (isVerified) {
            // Verified user - just wait, no action needed
            blockMessageText = `⏱️ You've reached your hourly chat limit. ${errorData.waitMinutes ? `Please wait ${errorData.waitMinutes} minutes to continue.` : 'Please try again later.'}`;
            promptVariant = 'wait_only';
          } else if (isRegistered) {
            // Registered but not verified - suggest verification
            blockMessageText = `⏱️ You've reached your hourly chat limit. ${errorData.waitMinutes ? `Please wait ${errorData.waitMinutes} minutes or verify via WhatsApp or Telegram for more access.` : 'Verify via WhatsApp or Telegram for more access.'}`;
            promptVariant = 'whatsapp_verify';
          } else {
            // Unregistered - suggest sign-up
            blockMessageText = `⏱️ You've reached your chat limit. Sign in to continue exploring Sri Lanka!`;
            promptVariant = 'google_auth';
          }
          
          // Add a clear blocking message
          const blockMessage: Message = {
            role: "bot",
            text: blockMessageText,
            messageId: `block_${Date.now()}`,
            suggestions: [],
            results: []
          };
          setMessages((prev) => [...prev, blockMessage]);
          
          // Show the registration prompt
          setRateLimitWaitMinutes(errorData.waitMinutes);
          setRegistrationVariant(promptVariant);
          setShowRegistrationPrompt(true);
          
          return;
        }
        throw new Error(`HTTP error! status: ${res.status}`);
      }

      const data = await res.json();
      
      // Record chat usage for unregistered users ONLY (server handles registered users atomically)
      if (tier === 'unregistered') {
        recordChatUsage();
      }

      const fullText = data.reply || "I found some information for you.";
      const suggestions = data.suggestions || [];
      const category = data.category || 'GENERAL';
      const metadata = data.metadata;
      const requestId = data.requestId;
      const hasMore = data.hasMore;


      // Add bot message with chat response
      const botMessage: Message = {
        role: "bot",
        text: fullText,
        messageId: botMessageId,
        suggestions,
        results: [], // Start empty for progressive reveal
        category,
        metadata,
        isCurated: data.isCurated // Track if this response is from curated
      };
      setMessages((prev) => [...prev, botMessage]);

      setLoading(false);

      // If there are more results coming, start polling for them
      if (hasMore && requestId) {
        pollForResults(requestId, botMessageId, fullText.length);
      } else if (data.results && data.results.length > 0) {
        // For curated responses with results, reveal them progressively around 50% through typing
        // Calculate timing: show at ~50% of typing time (8ms per character matches useTypingAnimation speed)
        const typingTime = fullText.length * 8; // 8ms per character (matches typing animation speed)
        const showPlacesAt = typingTime * 0.5; // Show at 50%
        
        setTimeout(() => {
          revealCardsProgressively(botMessageId, data.results);
        }, showPlacesAt);
      } else {
        // For curated responses without results, still mark typing as complete so suggestions show immediately
        if (data.isCurated) {
          setTimeout(() => {
            handleTypingComplete(botMessageId);
          }, 100);
        }
      }

    } catch (err) {
      // Error sending message
      const errorMessage: Message = {
        role: "bot",
        text: "⚠️ Server error. Please try again.",
        suggestions: [],
        results: []
      };
      setMessages((prev) => [...prev, errorMessage]);
      setLoading(false);
    }
  }

  // Poll for background search results
  async function pollForResults(requestId: string, messageId: string, textLength: number) {
    const maxPolls = 20; // 10 seconds max (500ms * 20)
    let pollCount = 0;

    const pollInterval = setInterval(async () => {
      pollCount++;

      try {
        const response = await fetch(`${API_BASE_URL}/api/chat/results/${requestId}`);
        const data = await response.json();

        if (data.status === 'ready') {
          clearInterval(pollInterval);

          // Calculate typing time (8ms per character matches useTypingAnimation speed)
          const typingTime = textLength * 8; // 8ms per character
          const elapsedTime = pollCount * 500; // Time spent polling
          const remainingTime = Math.max(0, typingTime - elapsedTime);

          // Wait for typing to finish, then reveal cards
          setTimeout(() => {
            revealCardsProgressively(messageId, data.results);
          }, remainingTime);
        } else if (pollCount >= maxPolls) {
          clearInterval(pollInterval);
        }
      } catch (err) {
        clearInterval(pollInterval);
      }
    }, 500);
  }

  // Reveal place cards one by one
  async function revealCardsProgressively(messageId: string, results: Result[]) {
    // Show suggestions right before place cards appear
    handleTypingComplete(messageId);

    for (let i = 0; i < results.length; i++) {
      await new Promise(resolve => setTimeout(resolve, 300)); // 300ms between cards

      setMessages((prev) => {
        const updated = [...prev];
        const msgIndex = updated.findIndex(m => m.messageId === messageId);
        if (msgIndex !== -1) {
          const currentResults = updated[msgIndex].results || [];
          updated[msgIndex] = {
            ...updated[msgIndex],
            results: [...currentResults, results[i]]
          };
        }
        return updated;
      });
    }

    // Update map markers after all cards are revealed (latest chat)
    if (results.length > 0) {
      results.forEach((r, idx) => {
      });
      
      // Parse geo_point if it's a JSON string (for curated responses)
      const resultsWithParsedGeo = results.map((r) => {
        let geoPoint = r.geo_point;
        if (typeof geoPoint === 'string') {
          try {
            geoPoint = JSON.parse(geoPoint);
          } catch (e) {
          }
        }
        return { ...r, geo_point: geoPoint };
      });
      
      const markers = resultsWithParsedGeo
        .filter((r): r is Result & { geo_point: { lat: number; lon: number } } =>
          r.geo_point != null && r.geo_point.lat != null && r.geo_point.lon != null
        )
        .map((r, idx) => {
          // Determine type based on entity_type or available fields
          let type: 'attraction' | 'food' | 'hotel' | 'experience' = 'attraction'; // default

          // Check entity_type or type field first
          if (r.entity_type === 'experience' || r.type === 'experience') {
            type = 'experience';
          } else if (r.entity_type === 'hotel' || r.type === 'hotel' || r.stay_description) {
            type = 'hotel';
          } else if (r.entity_type === 'food' || r.type === 'food' || r.food_description) {
            type = 'food';
          } else if (r.entity_type === 'attraction' || r.type === 'attraction' || r.cultural_significance) {
            type = 'attraction';
          }

          return {
            ...r,  // Spread all result properties first (for photo URLs, etc.)
            // Then override with required marker properties
            id: r.id ?? `marker-${idx}`,
            lat: r.geo_point.lat,
            lng: r.geo_point.lon,
            title: r.title,
            type: type,
        } as any;
      });

      latestChatMarkersRef.current = markers; // Save as latest markers
      setMapMarkers(markers);
      if (setCarouselData) {
        setCarouselData(markers);
      }
    }
  }

  // Mark message typing as complete - update the message object itself so it persists
  const handleTypingComplete = useCallback((messageId: string) => {
    // Update local typingComplete state (for immediate UI response)
    setTypingComplete(prev => ({ ...prev, [messageId]: true }));
    
    // Also update the message in context with hasTyped flag (for persistence across navigation)
    setMessages(prev => prev.map(msg => 
      msg.messageId === messageId ? { ...msg, hasTyped: true } : msg
    ));
  }, [setMessages]);

  // Process map markers for messages loaded from history (ONLY on initial load)
  const hasProcessedHistoryRef = useRef(false);
  
  useEffect(() => {
    // Only process history markers once after initial load
    // Skip if we've already processed history or if there are no messages
    if (hasProcessedHistoryRef.current || messages.length === 0) {
      return;
    }

    // Find all bot messages with results that are from history
    const historyMessagesWithResults = messages.filter(
      msg => msg.role === 'bot' && msg.isFromHistory && msg.results && msg.results.length > 0
    );

    if (historyMessagesWithResults.length === 0) {
      return; // No history messages with results
    }

    // Get the latest message with results
    const latestMessage = historyMessagesWithResults[historyMessagesWithResults.length - 1];
    const results = latestMessage.results || [];

    if (results.length === 0) {
      return;
    }

    // Create markers from results
    const markers = results
      .map((r) => {
        // Parse geo_point if it's a JSON string
        let geoPoint = r.geo_point;
        if (typeof geoPoint === 'string') {
          try {
            geoPoint = JSON.parse(geoPoint);
          } catch (e) {
            return null;
          }
        }
        return { ...r, geo_point: geoPoint };
      })
      .filter((r): r is Result & { geo_point: { lat: number; lon: number } } =>
        r != null && r.geo_point != null && r.geo_point.lat != null && r.geo_point.lon != null
      )
      .map((r, idx) => {
        // Determine type based on entity_type or available fields
        let type: 'attraction' | 'food' | 'hotel' | 'experience' = 'attraction'; // default

        // Check entity_type or type field first
        if (r.entity_type === 'experience' || r.type === 'experience') {
          type = 'experience';
        } else if (r.entity_type === 'hotel' || r.type === 'hotel' || r.stay_description) {
          type = 'hotel';
        } else if (r.entity_type === 'food' || r.type === 'food' || r.food_description) {
          type = 'food';
        } else if (r.entity_type === 'attraction' || r.type === 'attraction' || r.cultural_significance) {
          type = 'attraction';
        }

        return {
          ...r,  // Spread all result properties first (for photo URLs, etc.)
          // Then override with required marker properties
          id: r.id ?? `marker-${idx}`,
          lat: r.geo_point.lat,
          lng: r.geo_point.lon,
          title: r.title,
          type: type,
        } as any;
      });

    latestChatMarkersRef.current = markers; // Save as latest markers
    setMapMarkers(markers);
    if (setCarouselData) {
      setCarouselData(markers);
    }
    
    // Mark as processed so this doesn't run again
    hasProcessedHistoryRef.current = true;
  }, [messages, setMapMarkers, setCarouselData]); // Run when messages change, but ref prevents re-runs

  // Handle card hover - mark source as 'card' to prevent auto-scroll
  // Handle card hover (desktop) - only visual highlight, no map centering
  const handleCardMouseEnter = useCallback((id: string | number) => {
    setHoveredCardId(id);
    if (setHoveredId) {
      setHoveredId(id);
    }
  }, [setHoveredId]);

  const handleCardMouseLeave = useCallback(() => {
    setHoveredCardId(null);
    if (setHoveredId) {
      setHoveredId(null);
    }
  }, [setHoveredId]);

  // Handle opening detail panel
  const handleOpenDetail = useCallback((place: Result) => {
    setDetailPanelPlace(place);
  }, []);

  // Handle card touch (first touch on mobile - shows overlay and highlights marker)
  const handleCardTouch = useCallback((result: Result) => {
    const id = result.id || result.baseline_id;
    if (!id) return; // Safety check
    
    setTouchedPlaceId(id);
    
    // Highlight the marker on map
    highlightSourceRef.current = 'card';
    if (setHighlightedId) {
      setHighlightedId(id);
    }
  }, [setHighlightedId]);

  // Handle overlay click (second tap on mobile - opens detail panel)
  const handleOverlayClick = useCallback((place: Result) => {
    setDetailPanelPlace(place);
    setTouchedPlaceId(null); // Hide overlay
  }, []);

  // Update ref whenever activeCardIndex changes
  useEffect(() => {
    activeCardIndexRef.current = activeCardIndex;
  }, [activeCardIndex]);

  // Mobile carousel swipe handlers
  const handleCardTouchStart = useCallback((e: React.TouchEvent) => {
    cardStartXRef.current = e.touches[0].clientX;
    cardCurrentXRef.current = e.touches[0].clientX;
    setIsCardSwiping(true);
  }, []);

  const handleCardTouchMove = useCallback((e: React.TouchEvent) => {
    e.preventDefault(); // Prevent pull-to-refresh and default scroll behavior
    cardCurrentXRef.current = e.touches[0].clientX;
    const offset = cardCurrentXRef.current - cardStartXRef.current;
    setCardSwipeOffset(offset);
  }, []);

  const handleCardTouchEnd = useCallback(() => {
    setIsCardSwiping(false);
    const swipeDistance = cardCurrentXRef.current - cardStartXRef.current;
    const threshold = 50; // Minimum swipe distance to trigger card change
    
    if (Math.abs(swipeDistance) > threshold && latestChatMarkersRef.current.length > 0) {
      let newIndex: number;
      const maxIndex = latestChatMarkersRef.current.length - 1;
      
      if (swipeDistance > 0) {
        // Swiped right - go to previous card
        newIndex = activeCardIndexRef.current === 0 ? maxIndex : activeCardIndexRef.current - 1;
      } else {
        // Swiped left - go to next card
        newIndex = activeCardIndexRef.current === maxIndex ? 0 : activeCardIndexRef.current + 1;
      }
      
      setActiveCardIndex(newIndex);
      
      // Highlight the marker for the new card
      const marker = latestChatMarkersRef.current[newIndex];
      if (marker && setHighlightedId) {
        setHighlightedId(marker.id);
      }
    }
    
    setCardSwipeOffset(0);
    cardStartXRef.current = 0;
    cardCurrentXRef.current = 0;
  }, [setHighlightedId]);

  // Navigate to specific card index and optionally center map
  const navigateToCard = useCallback((index: number) => {
    if (latestChatMarkersRef.current.length > 0) {
      const maxIndex = latestChatMarkersRef.current.length - 1;
      const clampedIndex = Math.max(0, Math.min(index, maxIndex));
      const marker = latestChatMarkersRef.current[clampedIndex];
      
      setActiveCardIndex(clampedIndex);
      setCardSwipeOffset(0); // Reset swipe offset when clicking a card
      
      // Highlight the corresponding map marker
      if (marker && setHighlightedId) {
        setHighlightedId(marker.id);
      }
    }
  }, [setHighlightedId]);

  // Handle card click - add this old card marker to the map alongside latest chat markers
  const handleCardClick = useCallback((result: Result) => {
    // Parse geo_point if it's a string
    let geoData = result.geo_point;
    if (typeof geoData === 'string') {
      try {
        geoData = JSON.parse(geoData);
      } catch (e) {
        return;
      }
    }
    
    if (!geoData || !geoData.lat || !geoData.lon) {
      return;
    }

    // Determine type based on entity_type or available fields
    let type: 'attraction' | 'food' | 'hotel' | 'experience' = 'attraction';
    
    // Check entity_type or type field first
    if (result.entity_type === 'experience' || result.type === 'experience') {
      type = 'experience';
    } else if (result.entity_type === 'hotel' || result.type === 'hotel' || result.stay_description) {
      type = 'hotel';
    } else if (result.entity_type === 'food' || result.type === 'food' || result.food_description) {
      type = 'food';
    } else if (result.entity_type === 'attraction' || result.type === 'attraction' || result.cultural_significance) {
      type = 'attraction';
    }

    const oldCardMarker = {
      id: result.id ?? `marker-clicked-${Date.now()}`,
      lat: geoData.lat,
      lng: geoData.lon,
      title: result.title,
      type: type,
    };

    // Check if this marker already exists in latest markers
    const latestMarkers = latestChatMarkersRef.current;
    const markerExists = latestMarkers.some(m => m.id === oldCardMarker.id);

    if (markerExists) {
      // If it's already in latest markers, just keep the latest markers (highlight will show it)
      setMapMarkers(latestMarkers);
    } else {
      // Add the old card marker to the latest markers
      const combinedMarkers = [...latestMarkers, oldCardMarker];
      setMapMarkers(combinedMarkers);
    }
    
    // Highlight the marker on the map (triggers centering)
    if (setHighlightedId) {
      setHighlightedId(oldCardMarker.id);
    }
  }, [setMapMarkers, setHighlightedId]);

  // Handle map marker click - find result and open detail panel
  const handleMapMarkerClick = useCallback((markerId: string | number) => {
    // Find the marker index in the carousel
    const markerIndex = latestChatMarkersRef.current.findIndex(m => m.id === markerId);
    if (markerIndex !== -1) {
      // Scroll carousel to this marker
      navigateToCard(markerIndex);
    }
    
    // Search through all messages to find the result with this ID
    for (const message of messages) {
      if (message.role === 'bot' && message.results && Array.isArray(message.results)) {
        const result = message.results.find(r => r.id === markerId || r.baseline_id === markerId);
        if (result) {
          setDetailPanelPlace(result);
          return;
        }
      }
    }
  }, [messages]);

  // Register map marker click handler
  useEffect(() => {
    if (setOnMapMarkerClick) {
      setOnMapMarkerClick(() => handleMapMarkerClick);
    }
  }, [setOnMapMarkerClick, handleMapMarkerClick]);

  // Pass carousel state and handlers to Layout for rendering in map area
  useEffect(() => {
    if (setCarouselState) {
      setCarouselState({ activeCardIndex, cardSwipeOffset, isCardSwiping });
    }
  }, [activeCardIndex, cardSwipeOffset, isCardSwiping, setCarouselState]);

  useEffect(() => {
    if (setCarouselHandlers) {
      setCarouselHandlers({
        handleCardTouchStart,
        handleCardTouchMove,
        handleCardTouchEnd,
        navigateToCard,
        setHighlightedId, // Pass setHighlightedId so Layout can call it directly
        latestChatMarkersRef: latestChatMarkersRef.current  // Also pass markers directly
      });
    }
  }, [setCarouselHandlers, handleCardTouchStart, handleCardTouchMove, handleCardTouchEnd, navigateToCard, setHighlightedId]);

  useEffect(() => {
    if (setCarouselData) {
      setCarouselData(latestChatMarkersRef.current);
    }
  }, [setCarouselData]);

  // Handle suggestion clicks with proper index tracking
  function handleSuggestionClick(suggestionText: string, suggestionIndex: number, isFromGreeting: boolean = false, isFromCuratedResponse: boolean = false) {
    sendMessage(suggestionText, suggestionIndex, isFromGreeting, isFromCuratedResponse);
  }

  // Handle Quick Tips question selection - sends as curated message
  function handleQuickTipSelect(question: { id: string; question: string; response: string; tags: string[]; placeIds: string[] }) {
    // Send the question as if user typed it, but mark it as curated
    sendMessage(question.question, 0, false, true);
  }

  return (
    <div className="h-full w-full bg-gray-50 relative flex flex-col md:flex-col">
      {/* Quick Tips Button - MOBILE: center bottom of chat window */}
      <button
        onClick={() => setShowQuickTips(true)}
        className="md:hidden fixed bottom-36 left-1/2 -translate-x-1/2 z-40 
                   bg-gradient-to-r from-[#663399] to-[#14b8a6] text-white 
                   rounded-full px-4 py-2 shadow-lg text-sm font-medium
                   flex items-center gap-2 hover:shadow-xl transition-all active:scale-95"
        aria-label="Quick Tips"
      >
        <Lightbulb size={16} />
        <span>Quick Tips</span>
      </button>

      {/* Quick Tips Button - DESKTOP: centered at top of chat section */}
      <button
        onClick={() => setShowQuickTips(true)}
        className="hidden md:flex absolute top-3 left-1/2 -translate-x-1/2 z-30
                   bg-gradient-to-r from-[#663399] to-[#14b8a6] text-white 
                   rounded-full px-4 py-2 shadow-md text-sm font-medium
                   items-center gap-2 hover:shadow-lg transition-all hover:scale-[1.02]"
        aria-label="Quick Tips"
      >
        <Lightbulb size={16} />
        <span>Quick Tips</span>
      </button>

      {/* Quick Tips Overlay */}
      <QuickTipsOverlay 
        isOpen={showQuickTips}
        onClose={() => setShowQuickTips(false)}
        onQuestionSelect={handleQuickTipSelect}
        onShowRegistrationPrompt={(variant, waitMinutes, trigger) => {
          // Show registration prompt for Quick Tips limit with appropriate trigger
          setRateLimitWaitMinutes(waitMinutes);
          setRegistrationVariant(variant);
          if (trigger) {
            setRegistrationTrigger(trigger);
          }
          setShowRegistrationPrompt(true);
        }}
      />

      {/* Floating scroll to bottom button - positioned relative to chat container */}
      {showScrollButton && (
        <button
          onClick={() => {
            scrollToBottom();
            setShowScrollButton(false);
          }}
          className="fixed bottom-32 right-6 bg-[#663399] text-white p-3 rounded-full shadow-lg hover:bg-[#552288] transition-all z-40 animate-bounce md:absolute md:bottom-20 md:right-4"
          aria-label="Scroll to bottom"
        >
          <ChevronDown size={24} />
        </button>
      )}

      {/* Messages - takes up space above the fixed input */}
      <div ref={messagesContainerRef} className="flex-1 overflow-y-auto p-4 space-y-4 pb-20 md:pb-4">
        {/* Load More Button - only show if there are more messages and at least 5 conversations loaded */}
        {hasMoreHistory && messages.length >= 10 && (
          <div className="flex justify-center mb-4">
            <button
              onClick={loadOlderHistory}
              disabled={isLoadingHistory}
              className="px-4 py-2 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isLoadingHistory ? 'Loading...' : 'Load older messages'}
            </button>
          </div>
        )}
        
        {messages.map((msg, i) => {
          // Only apply typing animation to new messages (not from history) AND not already typed
          // Check both local state (typingComplete) AND persisted flag (msg.hasTyped) for reliability
          const hasAlreadyTyped = typingComplete[msg.messageId || ''] || msg.hasTyped || false;
          const isLastBotMessage = msg.role === "bot" && i === messages.length - 1 && !msg.isFromHistory && !hasAlreadyTyped;
          const shouldShowSuggestions = msg.isFromHistory || hasAlreadyTyped || !isLastBotMessage;
          
          return (
            <div key={msg.messageId || i} className="flex flex-col space-y-2">
              {/* Message bubble with typing animation for last bot message only */}
              <MessageBubble 
                message={msg} 
                isLastBotMessage={isLastBotMessage}
                sendMessage={sendMessage}
                onTypingUpdate={smartScrollToBottom}
                onTypingComplete={() => msg.messageId && handleTypingComplete(msg.messageId)}
                messageIndex={i}
              />

              {/* Structured Content */}
              {msg.role === "bot" && msg.structuredContent && (
                <div className="mt-3 space-y-2">
                  {msg.structuredContent.sections.map((section, sectionIndex) => (
                    <StructuredSectionComponent key={sectionIndex} section={section} />
                  ))}
                </div>
              )}

              {/* Suggestions - Only show after typing completes */}
              {msg.role === "bot" &&
                Array.isArray(msg.suggestions) &&
                msg.suggestions.length > 0 &&
                shouldShowSuggestions && (
                  <div className="flex flex-wrap gap-2 self-start">
                    {msg.suggestions.map((s, j) => (
                      <button
                        key={j}
                        onClick={() => handleSuggestionClick(s, j, msg.isFromGreeting, msg.isCurated)}
                        className="px-3 py-1 text-sm rounded-full border border-gray-700 text-gray-800 hover:bg-gray-100"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                )}

              {/* Results */}
              {msg.role === "bot" &&
                Array.isArray(msg.results) &&
                msg.results.length > 0 && (
                  <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-4 self-start mt-2">
                    {msg.results.map((r, idx) => {
                      // Check if this is a section marker
                      if (r._sectionMarker) {
                        return (
                          <div
                            key={r.id || `section-marker-${idx}`}
                            className="col-span-full mt-4 mb-2"
                          >
                            <h3 className="text-base font-semibold text-gray-700">
                              {r._sectionMarker}
                            </h3>
                          </div>
                        );
                      }

                      // Skip results without a title (invalid/empty cards)
                      if (!r.title) {
                        return null;
                      }

                      const id = r.id ?? idx;
                      const description = r.stay_description || r.food_description || r.cultural_significance || "";
                      
                      // Use entity_type directly from the backend (more reliable)
                      const type: 'hotel' | 'food' | 'attraction' = r.entity_type || 'attraction';
                      
                      // Allow clicking on cards from any message (old or latest)
                      const isOldCard = !isLastBotMessage;
                      
                      return (
                        <div
                          key={id}
                          ref={(el) => {
                            placeRefs.current[id] = el;
                          }}
                        >
                          <ChatPlaceCard
                            data-id={id}
                            baseline_id={r.baseline_id}
                            title={r.title}
                            city={r.city}
                            location_link={r.location_link}
                            rating={r.rating}
                            description={description}
                            photos={[
                              r.photo_1_cdn,
                              r.photo_2_cdn,
                              r.photo_3_cdn,
                              r.photo_4_cdn,
                              r.photo_5_cdn,
                            ].filter(Boolean)}
                            fullResult={r}
                            geo={
                              r.geo_point
                                ? { lat: r.geo_point.lat, lng: r.geo_point.lon }
                                : undefined
                            }
                            variant="preview"
                            onMouseEnter={() => handleCardMouseEnter(id)}
                            onMouseLeave={handleCardMouseLeave}
                            onClick={() => handleCardClick(r)}
                            onDoubleClick={() => handleOpenDetail(r)}
                            onTouch={() => handleCardTouch(r)}
                            showOverlay={touchedPlaceId === id}
                            onOverlayClick={() => handleOverlayClick(r)}
                            highlighted={highlightedId === id}
                            hovered={hoveredCardId === id}
                            className={`max-w-xs text-sm ${isOldCard ? 'cursor-pointer' : ''}`}
                            image_url={r.photo_1_cdn}
                            type={type}
                          />
                        </div>
                      );
                    })}
                  </div>
                )}
            </div>
          );
        })}

        {/* Show placeholder animation while loading */}
        {loading && <PlaceholderAnimation message={waitingMessage} />}

        {/* Scroll anchor */}
        <div ref={bottomRef} />
      </div>


      {/* Input - Fixed at bottom on mobile, relative on desktop */}
      <div className="fixed bottom-16 left-0 right-0 p-3 border-t bg-white flex z-50 md:relative md:bottom-auto md:left-auto md:right-auto md:z-10 md:flex-shrink-0">
        <input
          className="flex-1 border rounded-lg px-3 py-2 mr-2"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && sendMessage()}
          placeholder="Ask Hey SriLanka..."
        />
        <button
          className="bg-[#663399] text-white p-3 rounded-full hover:bg-[#552288] disabled:bg-[#C4B5D1] transition-colors shadow-md hover:shadow-lg flex items-center justify-center"
          onClick={() => sendMessage()}
          disabled={!input.trim()}
        >
          <ArrowUp size={20} strokeWidth={2.5} />
        </button>
      </div>

      {/* Place Detail Panel */}
      {detailPanelPlace && (
        <PlaceDetailPanel
          place={detailPanelPlace}
          onClose={() => setDetailPanelPlace(null)}
        />
      )}

      {/* Registration Prompt */}
      <RegistrationPrompt
        isOpen={showRegistrationPrompt}
        onClose={() => {
          setShowRegistrationPrompt(false);
          setRateLimitWaitMinutes(undefined);
          setRegistrationTrigger('chat_limit');
        }}
        variant={registrationVariant}
        trigger={registrationTrigger as any}
        waitMinutes={rateLimitWaitMinutes}
      />
    </div>
  );
}

/**
 * Message bubble component with typing animation for bot messages
 */
function MessageBubble({ 
  message, 
  isLastBotMessage,
  sendMessage,
  onTypingUpdate,
  onTypingComplete,
  messageIndex
}: { 
  message: Message; 
  isLastBotMessage: boolean;
  sendMessage: (text: string) => void;
  onTypingUpdate?: () => void;
  onTypingComplete?: () => void;
  messageIndex?: number;
}) {
  const { displayedText, isTyping } = useTypingAnimation(
    message.text,
    8,
    message.role === "bot" && isLastBotMessage
  );

  const textToShow = message.role === "bot" && isLastBotMessage ? displayedText : message.text;

  // Auto-scroll as text is being typed
  useEffect(() => {
    if (isTyping && onTypingUpdate) {
      onTypingUpdate();
    }
  }, [displayedText, isTyping, onTypingUpdate]);

  // ✨ FIX: Call onTypingComplete when typing animation finishes
  const prevIsTypingRef = useRef(isTyping);
  useEffect(() => {
    // Detect when typing just finished (was typing, now not typing)
    if (prevIsTypingRef.current && !isTyping && onTypingComplete) {
      onTypingComplete();
    }
    prevIsTypingRef.current = isTyping;
  }, [isTyping, onTypingComplete]);

  return (
    <div
      className={`p-3 rounded-2xl max-w-[75%] text-sm ${
        message.role === "user"
          ? "bg-gray-900 text-white self-end"
          : "bg-gray-200 text-gray-800 self-start"
      }`}
      style={{ whiteSpace: 'pre-line' }}
    >
      {textToShow}
      {message.role === "bot" && isTyping && (
        <span className="animate-pulse ml-1">|</span>
      )}
    </div>
  );
}
